import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { supabasePublic } from '@/integrations/supabase/publicClient';
import { toast } from '@/hooks/use-toast';
import {
  EstablishmentQuestion,
  QuestionScope,
  QUESTION_MAX_LENGTH,
  QUESTION_MAX_OPTIONS,
  QUESTION_OPTION_MAX_LENGTH,
} from '@/lib/establishmentQuestionUtils';

export interface EstablishmentQuestionInput {
  question: string;
  options: string[];
  allowMultiple: boolean;
  isRequired: boolean;
  scope: QuestionScope;
  specificDate?: string | null;
  weekday?: number | null;
  isActive: boolean;
}

function mapQuestion(row: {
  id: string;
  establishment_id: string;
  question: string;
  options: string[];
  allow_multiple: boolean;
  is_required: boolean;
  scope: string;
  specific_date: string | null;
  weekday: number | null;
  is_active: boolean;
}): EstablishmentQuestion {
  return {
    id: row.id,
    establishmentId: row.establishment_id,
    question: row.question,
    options: row.options ?? [],
    allowMultiple: row.allow_multiple,
    isRequired: row.is_required,
    scope: row.scope as QuestionScope,
    specificDate: row.specific_date,
    weekday: row.weekday,
    isActive: row.is_active,
  };
}

function toRow(input: EstablishmentQuestionInput) {
  return {
    question: input.question.trim().slice(0, QUESTION_MAX_LENGTH),
    options: input.options
      .map((o) => o.trim().slice(0, QUESTION_OPTION_MAX_LENGTH))
      .filter((o) => o !== '')
      .slice(0, QUESTION_MAX_OPTIONS),
    allow_multiple: input.allowMultiple,
    is_required: input.isRequired,
    scope: input.scope,
    specific_date: input.scope === 'date' ? input.specificDate ?? null : null,
    weekday: input.scope === 'weekday' ? input.weekday ?? null : null,
    is_active: input.isActive,
  };
}

export function establishmentQuestionsQueryKey(establishmentId: string | null | undefined) {
  return ['establishment-questions', establishmentId ?? null] as const;
}

/**
 * Question à choix multiple posée au client au moment de la réservation.
 * - `usePublicClient` : lecture anonyme (widget public), sans realtime.
 * - Les écritures passent toujours par le client authentifié (staff/admin).
 */
export function useEstablishmentQuestions(options: {
  establishmentId?: string | null;
  usePublicClient?: boolean;
}) {
  const { establishmentId, usePublicClient = false } = options;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: establishmentQuestionsQueryKey(establishmentId),
    queryFn: async (): Promise<EstablishmentQuestion[]> => {
      const client = usePublicClient ? supabasePublic : supabase;
      const { data, error } = await client
        .from('establishment_questions')
        .select('*')
        .eq('establishment_id', establishmentId!)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('[useEstablishmentQuestions] fetch error:', error);
        return [];
      }
      return (data ?? []).map(mapQuestion);
    },
    enabled: Boolean(establishmentId),
    staleTime: 1000 * 60 * 2,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: establishmentQuestionsQueryKey(establishmentId) });
  };

  const createQuestion = useMutation({
    mutationFn: async (input: EstablishmentQuestionInput) => {
      if (!establishmentId) throw new Error('establishmentId manquant');
      const { error } = await supabase
        .from('establishment_questions')
        .insert({ ...toRow(input), establishment_id: establishmentId });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'Question ajoutée', description: 'La question est enregistrée.' });
    },
    onError: (error: unknown) => {
      console.error('[useEstablishmentQuestions] create error:', error);
      toast({ title: 'Erreur', description: "Impossible d'ajouter la question.", variant: 'destructive' });
    },
  });

  const updateQuestion = useMutation({
    mutationFn: async ({ id, input }: { id: string; input: EstablishmentQuestionInput }) => {
      const { error } = await supabase
        .from('establishment_questions')
        .update(toRow(input))
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'Question mise à jour', description: 'Les modifications sont enregistrées.' });
    },
    onError: (error: unknown) => {
      console.error('[useEstablishmentQuestions] update error:', error);
      toast({ title: 'Erreur', description: 'Impossible de mettre à jour la question.', variant: 'destructive' });
    },
  });

  const deleteQuestion = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('establishment_questions').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'Question supprimée' });
    },
    onError: (error: unknown) => {
      console.error('[useEstablishmentQuestions] delete error:', error);
      toast({ title: 'Erreur', description: 'Impossible de supprimer la question.', variant: 'destructive' });
    },
  });

  return {
    questions: query.data ?? [],
    isLoading: query.isLoading,
    createQuestion: createQuestion.mutateAsync,
    updateQuestion: updateQuestion.mutateAsync,
    deleteQuestion: deleteQuestion.mutateAsync,
    isSaving: createQuestion.isPending || updateQuestion.isPending || deleteQuestion.isPending,
  };
}
